import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { LABEL } from '../../veriables';
import { TouchableButton } from '@components/common/Touchable';

const DISPLAY_MODES = {
  COMPACT: 'compact',
  LANDSCAPE: 'landscape',
  DEFAULT: 'default',
};

const STYLE_CONFIG = {
  [DISPLAY_MODES.COMPACT]: {
    cardPadding: {
      top: '0.5rem',
      bottom: '0.4rem',
    },
    balancePadding: {
      top: '4px',
      bottom: '4px',
    },
    balanceMargin: '0.4rem',
    remainingBalanceMargin: '1.5rem',
    button: {
      width: '240px',
      height: '48px',
      fontSize: '18px',
    },
    container: {
      bottom: '8px',
      left: '12px',
      right: '12px',
    },
  },
  [DISPLAY_MODES.LANDSCAPE]: {
    cardPadding: {
      top: '1rem',
      bottom: '0.5rem',
    },
    balancePadding: {
      top: '10px',
      bottom: '10px',
    },
    balanceMargin: '1rem',
    remainingBalanceMargin: '0',
    button: {
      width: '250px',
      height: '50px',
      fontSize: '22px',
    },
    container: {
      bottom: '1%',
      left: '0',
      right: 'auto',
    },
  },
  [DISPLAY_MODES.DEFAULT]: {
    cardPadding: {
      top: '1.5rem',
      bottom: '1.5rem',
    },
    balancePadding: {
      top: '40px',
      bottom: '60px',
    },
    balanceMargin: '2.5rem',
    remainingBalanceMargin: '0',
    button: {
      width: '236px',
      height: '68px',
      fontSize: '28px',
    },
    container: {
      bottom: '0',
      left: '0',
      right: 'auto',
    },
  },
};

const BUTTON_VARIANTS = {
  redeem: {
    border: 'none',
    marginRight: '0',
    getBackgroundColor: (theme) => theme?.secondaryBtn,
    getColor: (theme) => theme?.btn_font_color,
  },
  cancel: {
    border: '1px solid #2541A4',
    marginRight: '8px',
    getBackgroundColor: (theme) => theme?.OFF_Premise_Btn,
    getColor: (theme) => theme?.primary_text,
  },
};

const getDisplayMode = (isCompact, isLandscape) => {
  if (isCompact) return DISPLAY_MODES.COMPACT;
  if (isLandscape) return DISPLAY_MODES.LANDSCAPE;
  return DISPLAY_MODES.DEFAULT;
};

const getContainerBottom = (mode, showCheckSeatValue) => {
  if (mode === DISPLAY_MODES.COMPACT) {
    return STYLE_CONFIG[mode].container.bottom;
  }
  if (mode === DISPLAY_MODES.LANDSCAPE) {
    return STYLE_CONFIG[mode].container.bottom;
  }
  if (showCheckSeatValue) return '-40px';
  return STYLE_CONFIG[mode].container.bottom;
};

const checkHasCheckSeat = (showCheckSeat) => {
  if (!showCheckSeat || typeof showCheckSeat !== 'function') {
    return false;
  }
  return showCheckSeat();
};

const getButtonOpacity = (originalBalance) => {
  if (originalBalance === 0) return 0.5;
  return 1;
};

const getButtonCursor = (originalBalance) => {
  if (originalBalance === 0) return 'not-allowed';
  return 'pointer';
};

const GiftCardBalanceScreen = ({
  giftCardInfo,
  onDontUse,
  onRedeem,
  serverAppTheme,
  currencySymbol = '$',
  landscapePaymentMode = false,
  showCheckSeat,
}) => {
  const { balanceAmount, paymentAmount, displayAccountNumber } = giftCardInfo || {};
  const originalBalance = balanceAmount || 0;
  const redeemedAmount = paymentAmount || 0;
  const remainingBalance = originalBalance - redeemedAmount;

  const hasCheckSeat = checkHasCheckSeat(showCheckSeat);
  const isCompactMode = hasCheckSeat && landscapePaymentMode;
  const showCheckSeatVar = checkHasCheckSeat(showCheckSeat);

  const displayMode = getDisplayMode(isCompactMode, landscapePaymentMode);
  const modeConfig = STYLE_CONFIG[displayMode];

  const styles = useMemo(() => {
    const config = STYLE_CONFIG[displayMode];
    return {
      cardNumberContainer: {
        paddingTop: config.cardPadding.top,
        paddingBottom: config.cardPadding.bottom,
      },
      balanceContainer: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: config.balancePadding.top,
        paddingBottom: config.balancePadding.bottom,
      },
      balanceItemMargin: {
        marginBottom: config.balanceMargin,
      },
      remainingBalanceMargin: {
        marginBottom: config.remainingBalanceMargin,
      },
      buttonContainer: {
        position: 'absolute',
        bottom: getContainerBottom(displayMode, showCheckSeatVar),
        left: config.container.left,
        right: config.container.right,
        padding: '0',
        justifyContent: 'center',
      },
      textStyle: {
        color: serverAppTheme?.primary_text,
        fontSize: '24px',
        fontWeight: '400',
      },
      amountStyle: {
        color: serverAppTheme?.primary_text,
        fontSize: '36px',
        fontWeight: 'bold',
      },
    };
  }, [displayMode, showCheckSeatVar, serverAppTheme]);

  const getButtonStyle = (variant) => {
    const variantConfig = BUTTON_VARIANTS[variant];
    const baseStyle = {
      width: modeConfig.button.width,
      height: modeConfig.button.height,
      fontSize: modeConfig.button.fontSize,
      backgroundColor: variantConfig.getBackgroundColor(serverAppTheme),
      color: variantConfig.getColor(serverAppTheme),
      border: variantConfig.border,
      borderRadius: "4px",
      marginRight: variantConfig.marginRight,
      fontFamily: "Poppins",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: "1.2",
    };

    if (variant === 'redeem') {
      return {
        ...baseStyle,
        opacity: getButtonOpacity(originalBalance),
        cursor: getButtonCursor(originalBalance),
      };
    }

    return baseStyle;
  };

  const renderBalanceItem = (label, amount, customStyle = {}) => (
    <div className="text-center" style={{ ...styles.balanceItemMargin, ...customStyle }}>
      <div className="mb-1" style={styles.textStyle}>
        {label}
      </div>
      <div style={styles.amountStyle}>
        {currencySymbol}{amount.toFixed(2)}
      </div>
    </div>
  );

  return (
    <div className="col-12 px-0" style={{ position: 'relative', minHeight: '100%' }}>
      <div className="col-12 text-center" style={styles.cardNumberContainer}>
        <div style={{ color: serverAppTheme?.primary_text, fontSize: '24px', fontWeight: '500' }}>
          Card : {displayAccountNumber || ''}
        </div>
      </div>

      <div className="col-12 px-4 h-72" style={styles.balanceContainer}>
        {renderBalanceItem(LABEL().GS_ORIGINAL_BALANCE || 'Original Balance', originalBalance)}
        {renderBalanceItem(LABEL().GS_REDEEMED_AMOUNT || 'Redeemed Amount', redeemedAmount)}
        {renderBalanceItem(
          LABEL().GS_REMAINING_BALANCE || 'Remaining Balance',
          remainingBalance,
          styles.remainingBalanceMargin
        )}
      </div>

      <div className="col-12 d-flex" style={styles.buttonContainer}>
        <TouchableButton
          type="button"
          className={cn("btn-ripple fw-600 od-cancel-btn-bg-color od-cancel-btn-font-color od-cancel-btn-st-color")}
          style={getButtonStyle('cancel')}
          onClick={onDontUse}
          rippleEffect={true}
        >
          {LABEL().GS_DONT_USE || "Don't Use"}
        </TouchableButton>

        <TouchableButton
          type="button"
          className={cn("btn-ripple fw-600 od-primary-btn-bg-color od-primary-btn-font-color")}
          style={getButtonStyle('redeem')}
          onClick={onRedeem}
          rippleEffect={true}
          disabled={originalBalance === 0}
        >
          {LABEL().GS_REDEEM || 'Redeem'}
        </TouchableButton>
      </div>
    </div>
  );
};

GiftCardBalanceScreen.propTypes = {
  giftCardInfo: PropTypes.shape({
    balanceAmount: PropTypes.number,
    paymentAmount: PropTypes.number,
    displayAccountNumber: PropTypes.string,
  }),
  onDontUse: PropTypes.func,
  onRedeem: PropTypes.func,
  serverAppTheme: PropTypes.shape({
    primary_text: PropTypes.string,
    OFF_Premise_Btn: PropTypes.string,
    secondaryBtn: PropTypes.string,
    btn_font_color: PropTypes.string,
  }),
  currencySymbol: PropTypes.string,
  landscapePaymentMode: PropTypes.bool,
  showCheckSeat: PropTypes.func,
};

GiftCardBalanceScreen.defaultProps = {
  giftCardInfo: {},
  onDontUse: () => {},
  onRedeem: () => {},
  serverAppTheme: {},
  currencySymbol: '$',
  landscapePaymentMode: false,
  showCheckSeat: () => false,
};

export default GiftCardBalanceScreen;
